/**
 * Soundfont Audio Engine for MusicSheet
 * Loads high-quality real acoustic instrument samples on-demand from FluidR3 GM soundfonts via jsDelivr CDN (with CORS headers)
 * and falls back gracefully to Web Audio synthesized harmonics when offline.
 */

const SOUNDFONT_BASE_URL = 'https://cdn.jsdelivr.net/gh/gleitz/midi-js-soundfonts@gh-pages/FluidR3_GM/';

// List of available instruments with user-friendly labels and icons
export const AVAILABLE_INSTRUMENTS = [
  { id: 'acoustic_grand_piano', label: 'Piano de Cauda', icon: '🎹', category: 'Teclas' },
  { id: 'bright_acoustic_piano', label: 'Piano Brilhante', icon: '🎹', category: 'Teclas' },
  { id: 'harpsichord', label: 'Cravo (Harpsichord)', icon: '🎼', category: 'Teclas' },
  { id: 'church_organ', label: 'Órgão de Tubos', icon: '⛪', category: 'Teclas' },
  { id: 'acoustic_guitar_nylon', label: 'Violão (Nylon)', icon: '🎸', category: 'Cordas' },
  { id: 'acoustic_guitar_steel', label: 'Violão (Aço)', icon: '🎸', category: 'Cordas' },
  { id: 'violin', label: 'Violino', icon: '🎻', category: 'Cordas' },
  { id: 'cello', label: 'Violoncelo', icon: '🎻', category: 'Cordas' },
  { id: 'string_ensemble_1', label: 'Orquestra de Cordas', icon: '🎻', category: 'Cordas' },
  { id: 'flute', label: 'Flauta Transversal', icon: '🪈', category: 'Sopros' },
  { id: 'trumpet', label: 'Trompete', icon: '🎺', category: 'Metais' },
  { id: 'drum_kit', label: 'Bateria', icon: '🥁', category: 'Percussão' }
];

/**
 * Drum kit map: staff position (treble-equivalent pitch), notehead and GM id.
 * Positions follow common drum-chart convention on a percussion staff.
 */
export const DRUM_PIECES = [
  { id: 'kick', step: 'F', octave: 4, head: 'normal', gm: 36 },
  { id: 'snare', step: 'C', octave: 5, head: 'normal', gm: 38 },
  { id: 'hihat', step: 'G', octave: 5, head: 'x', gm: 42 },
  { id: 'hihatOpen', step: 'G', octave: 5, head: 'x', gm: 46 },
  { id: 'tomHigh', step: 'E', octave: 5, head: 'normal', gm: 50 },
  { id: 'tomMid', step: 'D', octave: 5, head: 'normal', gm: 47 },
  { id: 'tomFloor', step: 'A', octave: 4, head: 'normal', gm: 43 },
  { id: 'crash', step: 'A', octave: 5, head: 'x', gm: 49 },
  { id: 'ride', step: 'F', octave: 5, head: 'x', gm: 51 }
];

export function drumPieceById(id) {
  return DRUM_PIECES.find(p => p.id === id) || null;
}

// Match a clicked pitch to the nearest drum piece (diatonic distance)
export function drumPieceForPitch(step, octave) {
  const pitchOrder = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const dia = (octave || 4) * 7 + pitchOrder.indexOf((step || 'C').toUpperCase());
  let best = DRUM_PIECES[0];
  let bestDist = Infinity;
  for (const p of DRUM_PIECES) {
    const pd = (p.octave || 4) * 7 + pitchOrder.indexOf(p.step);
    const dist = Math.abs(pd - dia);
    if (dist < bestDist) {
      bestDist = dist;
      best = p;
    }
  }
  return best;
}

let cachedNoiseBuffer = null;
function getNoiseBuffer(ctx) {
  if (cachedNoiseBuffer) return cachedNoiseBuffer;
  const len = Math.floor(ctx.sampleRate * 2);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  cachedNoiseBuffer = buf;
  return buf;
}

function drumNoiseHit(ctx, { when = 0, dur = 0.2, type = 'bandpass', freq = 2000, q = 1, gainLevel = 0.8 }) {
  const t = ctx.currentTime + when;
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  src.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = freq;
  filter.Q.value = q;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.linearRampToValueAtTime(gainLevel, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  src.start(t);
  src.stop(t + dur + 0.05);
}

function drumToneHit(ctx, { when = 0, from = 150, to = 50, dur = 0.25, type = 'sine', gainLevel = 0.8 }) {
  const t = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + dur);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.linearRampToValueAtTime(gainLevel, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + dur + 0.05);
}

/**
 * Synthesized drum kit (offline-safe, no samples). `when` offsets in seconds.
 */
export function playDrumSound(pieceId, { durationSec = 0.3, gainLevel = 0.8, when = 0 } = {}) {
  const ctx = getSharedAudioContext();
  if (!ctx) return;
  const g = Math.max(0.05, Math.min(1, gainLevel));
  switch (pieceId) {
    case 'kick':
      drumToneHit(ctx, { when, from: 160, to: 42, dur: 0.28, gainLevel: g });
      drumNoiseHit(ctx, { when, dur: 0.03, type: 'highpass', freq: 4000, gainLevel: g * 0.5 });
      break;
    case 'snare':
      drumNoiseHit(ctx, { when, dur: 0.18, type: 'bandpass', freq: 1800, q: 0.9, gainLevel: g });
      drumToneHit(ctx, { when, from: 190, to: 120, dur: 0.1, type: 'triangle', gainLevel: g * 0.6 });
      break;
    case 'hihat':
      drumNoiseHit(ctx, { when, dur: 0.05, type: 'highpass', freq: 8000, gainLevel: g * 0.7 });
      break;
    case 'hihatOpen':
      drumNoiseHit(ctx, { when, dur: Math.max(0.3, durationSec), type: 'highpass', freq: 7500, gainLevel: g * 0.7 });
      break;
    case 'tomHigh':
      drumToneHit(ctx, { when, from: 220, to: 110, dur: 0.25, gainLevel: g });
      break;
    case 'tomMid':
      drumToneHit(ctx, { when, from: 175, to: 90, dur: 0.28, gainLevel: g });
      break;
    case 'tomFloor':
      drumToneHit(ctx, { when, from: 130, to: 62, dur: 0.32, gainLevel: g });
      break;
    case 'crash':
      drumNoiseHit(ctx, { when, dur: Math.max(0.9, durationSec), type: 'highpass', freq: 4500, gainLevel: g * 0.8 });
      drumNoiseHit(ctx, { when, dur: 0.5, type: 'bandpass', freq: 9000, q: 2, gainLevel: g * 0.3 });
      break;
    case 'ride':
      drumNoiseHit(ctx, { when, dur: Math.max(0.45, durationSec), type: 'bandpass', freq: 3200, q: 6, gainLevel: g * 0.6 });
      drumToneHit(ctx, { when, from: 620, to: 600, dur: 0.15, type: 'sine', gainLevel: g * 0.25 });
      break;
    default:
      drumToneHit(ctx, { when, from: 200, to: 100, dur: durationSec, gainLevel: g });
      break;
  }
}

// Cache decoded audio buffers: Map<`${instrumentId}-${noteName}`, AudioBuffer> (L1, memória)
const audioBufferCache = new Map();
const loadingPromises = new Map();

// L2 persistente (sobrevive ao reload): Cache Storage com os mp3 crus.
// Torna a 2ª abertura instantânea e funciona sem service-worker.
const SOUNDFONT_CACHE_NAME = 'soundfont-v1';

async function openSampleCache() {
  try {
    if (typeof caches !== 'undefined' && caches.open) {
      return await caches.open(SOUNDFONT_CACHE_NAME);
    }
  } catch {
    // Cache Storage indisponível (contexto inseguro, etc.) — segue só com L1 + rede
  }
  return null;
}

async function fetchAndCacheSample(cache, url) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  // Guarda o cru no L2 antes de decodificar (clone: um vai pro cache, outro decodifica)
  try {
    if (cache) await cache.put(url, response.clone());
  } catch {
    // quota/erro de escrita não pode quebrar a reprodução
  }
  return response;
}

let audioContext = null;

export function getSharedAudioContext() {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioContext = new AudioContextClass();
    }
  }
  if (audioContext && audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
}

/**
 * Aquece o AudioContext compartilhado (cria + resume).
 * Chamar ao abrir o afinador — ainda dentro da cadeia do gesto do usuário —
 * para o primeiro toque da corda não pagar o custo de criação/resume.
 */
export async function warmUpPlayback() {
  const ctx = getSharedAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
      await ctx.resume();
    }
  } catch {
    // ignora: o resume será tentado de novo no momento do toque
  }
}

// Tempo máximo que o toque da corda espera pelo sample real antes de
// cair para o sintetizado instantâneo (o download continua em background
// e o próximo toque já usa o sample). Garante resposta tátil imediata.
const SAMPLE_WAIT_MS = 150;

/**
 * Standard note name representation for Soundfont URL (e.g. C4, Db4, F#4 -> Fs4)
 */
export function getSoundfontNoteName(step, octave, accidental = '') {
  let s = (step || 'C').toUpperCase();
  let oct = octave !== undefined ? octave : 4;

  if (accidental === '#' || accidental === '^') {
    return `${s}s${oct}`;
  } else if (accidental === 'b' || accidental === '_') {
    const flatToSharp = {
      'Db': 'Cs',
      'Eb': 'Ds',
      'Gb': 'Fs',
      'Ab': 'Gs',
      'Bb': 'As',
      'Cb': 'B',
      'Fb': 'E'
    };
    if (s === 'C') return `B${oct - 1}`;
    if (s === 'F') return `E${oct}`;
    const mapped = flatToSharp[`${s}b`];
    if (mapped) return `${mapped}${oct}`;
  }

  return `${s}${oct}`;
}

/**
 * Preload sample buffer for an individual note
 */
export async function loadNoteSample(instrumentId, noteName) {
  const cacheKey = `${instrumentId}-${noteName}`;
  if (audioBufferCache.has(cacheKey)) {
    return audioBufferCache.get(cacheKey);
  }

  if (loadingPromises.has(cacheKey)) {
    return loadingPromises.get(cacheKey);
  }

  const ctx = getSharedAudioContext();
  if (!ctx) return null;

  const url = `${SOUNDFONT_BASE_URL}${instrumentId}-mp3/${noteName}.mp3`;

  const promise = (async () => {
    try {
      const cache = await openSampleCache();
      let response = null;

      // 1) Tenta o L2 persistente primeiro (reload não refaz download)
      if (cache) {
        try {
          const hit = await cache.match(url);
          if (hit) response = hit;
        } catch {
          response = null;
        }
      }

      // 2) Rede + grava no L2
      if (!response) {
        response = await fetchAndCacheSample(cache, url);
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      audioBufferCache.set(cacheKey, audioBuffer);
      return audioBuffer;
    } catch (err) {
      return null;
    } finally {
      loadingPromises.delete(cacheKey);
    }
  })();

  loadingPromises.set(cacheKey, promise);
  return promise;
}

/**
 * Preload an entire batch of notes for seamless playback
 */
export async function preloadNotes(notesList = [], instrumentId = 'acoustic_grand_piano') {
  if (instrumentId === 'drum_kit') return [];
  const promises = [];
  notesList.forEach(n => {
    if (!n.isRest && !n.isHidden) {
      const name = getSoundfontNoteName(n.step, n.octave, n.accidental);
      promises.push(loadNoteSample(instrumentId, name));
    }
  });
  return Promise.all(promises);
}

const STEP_BY_SEMITONE = [
  ['C', ''], ['C', '#'], ['D', ''], ['D', '#'], ['E', ''],
  ['F', ''], ['F', '#'], ['G', ''], ['G', '#'], ['A', ''], ['A', '#'], ['B', ''],
];

/**
 * Converte frequência (Hz) no sample SoundFont mais próximo (ex. 329.63 -> E4).
 * Usado pelos tons de referência do afinador.
 */
export function noteNameForFrequency(freq) {
  if (!freq || freq <= 0 || !isFinite(freq)) return null;
  const midi = Math.round(69 + 12 * (Math.log(freq / 440) / Math.log(2)));
  const semitone = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const [step, accidental] = STEP_BY_SEMITONE[semitone];
  return getSoundfontNoteName(step, octave, accidental);
}

/**
 * Toca um tom de referência por frequência com sample real.
 * `instrumentId: 'synth'` pula direto para o fallback sintetizado.
 * Retorna true quando tocou sample real, false quando usou fallback.
 */
export async function playReferenceFrequency(freq, instrumentId = 'acoustic_guitar_steel', durationSec = 2.5, gainLevel = 0.8) {
  const noteName = noteNameForFrequency(freq);
  if (!noteName || instrumentId === 'synth') {
    if (noteName) {
      const m = noteName.match(/^([A-G])(s?)(\d+)$/);
      if (m) {
        playSynthesizedFallback(
          getSharedAudioContext(), m[1], Number(m[3]), m[2] === 's' ? '#' : '',
          durationSec, instrumentId, gainLevel,
        );
      }
    }
    return false;
  }
  const cacheKey = `${instrumentId}-${noteName}`;
  let audioBuffer = audioBufferCache.get(cacheKey);
  if (!audioBuffer) {
    // Espera limitada pelo sample real; se estourar, toca o sintetizado na
    // hora (o download continua em background via loadingPromises e o
    // próximo toque já usa o sample). Garante resposta tátil imediata.
    audioBuffer = await Promise.race([
      loadNoteSample(instrumentId, noteName),
      new Promise((resolve) => setTimeout(() => resolve(null), SAMPLE_WAIT_MS)),
    ]);
  }
  if (audioBuffer) {
    const ctx = getSharedAudioContext();
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    const gainNode = ctx.createGain();
    const t = ctx.currentTime;
    gainNode.gain.setValueAtTime(gainLevel, t);
    gainNode.gain.setValueAtTime(gainLevel * 0.9, t + durationSec);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, t + durationSec + 0.6);
    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.start(t);
    source.stop(t + durationSec + 0.7);
    return true;
  }
  const m = noteName.match(/^([A-G])(s?)(\d+)$/);
  if (m) {
    playSynthesizedFallback(
      getSharedAudioContext(), m[1], Number(m[3]), m[2] === 's' ? '#' : '',
      durationSec, instrumentId, gainLevel,
    );
  }
  return false;
}

/**
 * Pré-carrega os samples das frequências (ex. cordas da afinação) para o
 * primeiro toque ser instantâneo. Com o L2, o reload nem refaz download.
 */
export async function preloadReferenceNotes(freqs = [], instrumentId = 'acoustic_guitar_steel') {
  if (instrumentId === 'synth') return [];
  const names = [...new Set(freqs.map(noteNameForFrequency).filter(Boolean))];
  return Promise.all(names.map((n) => loadNoteSample(instrumentId, n)));
}

/**
 * Limpa o cache persistente de samples (manutenção / liberar espaço).
 */
export async function clearSoundfontCache() {
  audioBufferCache.clear();
  try {
    if (typeof caches !== 'undefined' && caches.delete) {
      await caches.delete(SOUNDFONT_CACHE_NAME);
    }
  } catch {
    // ignora
  }
}

/**
 * Play a note with real soundfont sample, with automatic fallback to Web Audio oscillator
 */
export async function playSoundfontNote(step, octave, accidental = '', durationSec = 0.5, instrumentId = 'acoustic_grand_piano', gainLevel = 0.8) {
  const ctx = getSharedAudioContext();
  if (!ctx) return;

  const noteName = getSoundfontNoteName(step, octave, accidental);
  let audioBuffer = audioBufferCache.get(`${instrumentId}-${noteName}`);

  if (!audioBuffer) {
    loadNoteSample(instrumentId, noteName).then(buf => {
      if (buf) audioBufferCache.set(`${instrumentId}-${noteName}`, buf);
    });
  }

  if (audioBuffer) {
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;

    const gainNode = ctx.createGain();
    const startTime = ctx.currentTime;
    const releaseTime = Math.min(0.8, Math.max(0.2, durationSec * 0.4));

    gainNode.gain.setValueAtTime(gainLevel, startTime);
    gainNode.gain.setValueAtTime(gainLevel * 0.9, startTime + durationSec);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startTime + durationSec + releaseTime);

    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    source.start(startTime);
    source.stop(startTime + durationSec + releaseTime + 0.05);
  } else {
    playSynthesizedFallback(ctx, step, octave, accidental, durationSec, instrumentId, gainLevel);
  }
}

/**
 * Synthesized Harmonic Fallback with distinct ADSR profiles
 */
function playSynthesizedFallback(ctx, step, octave, accidental, durationSec, instrumentId, gainLevel) {
  const midi = getMidiNumberFromStep(step, octave, accidental);
  const freq = 440 * Math.pow(2, (midi - 69) / 12);

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.frequency.setValueAtTime(freq, ctx.currentTime);

  if (instrumentId.includes('organ')) {
    osc.type = 'sawtooth';
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.25 * gainLevel, ctx.currentTime + 0.04);
    gain.gain.setValueAtTime(0.25 * gainLevel, ctx.currentTime + durationSec * 0.9);
    gain.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + durationSec);
  } else if (instrumentId.includes('guitar')) {
    osc.type = 'triangle';
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.35 * gainLevel, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec);
  } else if (instrumentId.includes('violin') || instrumentId.includes('string') || instrumentId.includes('cello')) {
    osc.type = 'sawtooth';
    filter.type = 'lowpass';
    filter.frequency.value = 1400;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.22 * gainLevel, ctx.currentTime + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec + 0.1);
  } else if (instrumentId.includes('flute')) {
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.28 * gainLevel, ctx.currentTime + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec);
  } else {
    osc.type = 'triangle';
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3 * gainLevel, ctx.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec);
  }

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + durationSec + 0.15);
}

const NOTE_SEMITONES = { 'C': 0, 'D': 2, 'E': 4, 'F': 5, 'G': 7, 'A': 9, 'B': 11 };
function getMidiNumberFromStep(step, octave, accidental = '') {
  const base = 12 + (octave * 12) + (NOTE_SEMITONES[(step || 'C').toUpperCase()] || 0);
  let alt = 0;
  if (accidental === '#' || accidental === '^') alt = 1;
  else if (accidental === 'b' || accidental === '_') alt = -1;
  return base + alt;
}
